import { useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './Resinas.module.css';
import ResinaModal from './ResinaModal';
import {
  listar,
  criar,
  atualizar,
  COLECOES,
} from '../../../services/firestore';

// ====================================================================
// CADASTRO DE RESINAS
// Conectado ao Firestore — colecao: resinas  (Bloco 3.2)
// Estrutura/visual identicos a versao aprovada; muda so a fonte de dados.
// ====================================================================

export default function Resinas() {
  const navigate = useNavigate();

  const [resinas, setResinas] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');
  const [salvando, setSalvando] = useState(false);

  const [busca, setBusca] = useState('');
  const [filtroStatus, setFiltroStatus] = useState('');

  const [modalAberto, setModalAberto] = useState(false);
  const [resinaEditando, setResinaEditando] = useState(null);

  // ------------------------------------------------------------------
  // Carregar resinas do Firestore
  // ------------------------------------------------------------------
  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const dados = await listar(COLECOES.RESINAS, { ordenarPor: 'nome' });
      setResinas(dados);
    } catch (e) {
      setErro(e.message || 'Erro ao carregar resinas.');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const resinasFiltradas = useMemo(() => {
    return resinas.filter((r) => {
      if (busca && !r.nome.toLowerCase().includes(busca.toLowerCase())) return false;
      if (filtroStatus === 'Ativos' && !r.ativo) return false;
      if (filtroStatus === 'Inativos' && r.ativo) return false;
      return true;
    });
  }, [resinas, busca, filtroStatus]);

  const totalAtivos = resinas.filter((r) => r.ativo).length;
  const totalInativos = resinas.length - totalAtivos;

  function abrirNovo() {
    setResinaEditando(null);
    setModalAberto(true);
  }

  function abrirEditar(resina) {
    setResinaEditando(resina);
    setModalAberto(true);
  }

  function fecharModal() {
    setModalAberto(false);
    setResinaEditando(null);
  }

  // ------------------------------------------------------------------
  // Salvar (criar ou atualizar) no Firestore
  // ------------------------------------------------------------------
  async function salvar(dados) {
    setSalvando(true);
    setErro('');
    try {
      if (resinaEditando) {
        await atualizar(COLECOES.RESINAS, resinaEditando.id, dados);
      } else {
        await criar(COLECOES.RESINAS, { ...dados, ativo: dados.ativo ?? true });
      }
      fecharModal();
      await carregar(); // recarrega a lista do banco
    } catch (e) {
      setErro(e.message || 'Erro ao salvar a resina.');
    } finally {
      setSalvando(false);
    }
  }

  // ------------------------------------------------------------------
  // Ativar / Inativar no Firestore
  // ------------------------------------------------------------------
  async function toggleAtivo(id) {
    const alvo = resinas.find((r) => r.id === id);
    if (!alvo) return;
    setErro('');
    // atualizacao otimista (UI responde na hora; reverte se falhar)
    setResinas((prev) =>
      prev.map((r) => (r.id === id ? { ...r, ativo: !r.ativo } : r))
    );
    try {
      await atualizar(COLECOES.RESINAS, id, { ativo: !alvo.ativo });
    } catch (e) {
      setErro(e.message || 'Erro ao alterar o status da resina.');
      // reverte
      setResinas((prev) =>
        prev.map((r) => (r.id === id ? { ...r, ativo: alvo.ativo } : r))
      );
    }
  }

  return (
    <>
      <div className={styles.topbar}>
        <div className={styles.pageTitle}>Cadastro de Resinas</div>
        <button className={styles.btnVoltar} onClick={() => navigate('/cadastros')}>
          ← Voltar para Cadastros
        </button>
      </div>

      {erro && (
        <div
          style={{
            background: '#fdecea',
            color: '#b51013',
            border: '1px solid #f5c6cb',
            borderRadius: 8,
            padding: '10px 14px',
            margin: '0 0 14px',
            fontSize: 14,
          }}
        >
          {erro}
        </div>
      )}

      <div className={styles.toolbar}>
        <input
          className={styles.toolbarInput}
          placeholder="🔍 Buscar resina..."
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
        />
        <select
          className={styles.toolbarSelect}
          value={filtroStatus}
          onChange={(e) => setFiltroStatus(e.target.value)}
        >
          <option value="">Todos os status</option>
          <option value="Ativos">Ativos</option>
          <option value="Inativos">Inativos</option>
        </select>
        <button className={styles.btnNovo} onClick={abrirNovo}>
          + Nova resina
        </button>
      </div>

      <div className={styles.tabelaWrap}>
        <div className={styles.tabelaHead}>
          <span className={styles.tabelaCount}>
            {resinas.length} resinas cadastradas ({totalAtivos} ativas · {totalInativos}{' '}
            {totalInativos === 1 ? 'inativa' : 'inativas'})
          </span>
          <span className={styles.tabelaHint}>Clique em ✏️ para editar</span>
        </div>

        <div className={styles.colHeader}>
          <div>Nome</div>
          <div>Descrição</div>
          <div></div>
        </div>

        {carregando ? (
          <div className={styles.semResultados}>Carregando resinas...</div>
        ) : resinasFiltradas.length === 0 ? (
          <div className={styles.semResultados}>
            {resinas.length === 0
              ? 'Nenhuma resina cadastrada ainda. Clique em "+ Nova resina" para começar.'
              : 'Nenhuma resina encontrada com os filtros aplicados.'}
          </div>
        ) : (
          resinasFiltradas.map((resina) => (
            <div
              key={resina.id}
              className={`${styles.row} ${!resina.ativo ? styles.inativo : ''}`}
            >
              <div className={styles.nome}>{resina.nome}</div>
              <div className={styles.descricao}>
                {resina.descricao || <span style={{ color: '#bbb' }}>—</span>}
              </div>
              <div className={styles.acoes}>
                <span
                  className={`${styles.statusTag} ${resina.ativo ? styles.stAtivo : styles.stInativo}`}
                >
                  {resina.ativo ? 'Ativo' : 'Inativo'}
                </span>
                <button
                  className={`${styles.btnAcao} ${styles.editar}`}
                  title="Editar"
                  onClick={() => abrirEditar(resina)}
                >
                  ✏️
                </button>
                <button
                  className={`${styles.btnAcao} ${styles.inativar}`}
                  title={resina.ativo ? 'Inativar' : 'Reativar'}
                  onClick={() => toggleAtivo(resina.id)}
                >
                  {resina.ativo ? '🚫' : '✅'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {modalAberto && (
        <ResinaModal
          resina={resinaEditando}
          onSalvar={salvar}
          onCancelar={fecharModal}
          salvando={salvando}
        />
      )}
    </>
  );
}